import * as companyBillService from "../../services/companyBill";
import * as customerBillService from "../../services/customerBill";
import * as settings from "../../services/settings";

import { Button, Col, InputNumber, Popconfirm, Row, Spin, Table, notification } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ModalDestinationService } from "./modal/ModalDestinationService";
import dayjs from "dayjs";
import { formatNumber } from "../../utils/number";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import "../../../styles/createOriginPackageService.css";

const initPage = { limit: 5, page: 1, total: 1 };
let promiseAwait;
export const CreateOriginPackageTransportationService = ({
  isCompanyBill,
  companyBillItemId,
  dataCompanyBill,
  onItemsChange,
  translations,
}) => {
  const { t } = useTranslation(translations);
  const { hasPermission, isSuperuser } = usePermission();
  const hasFetchedRef = useRef(false);
  const { accessToken } = useAccessToken();
  const [loading, setLoading] = useState(false);
  const [companyBillItem, setCompanyBillItem] = useState([]);
  const [search, setSearch] = useState(initPage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [item, setItem] = useState();
  const [editOriginPackageServiceData, setEditOriginPackageServiceData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [total, setTotal] = useState(0);
  const [sumTotal, setSumTotal] = useState(0);

  const [editRows, setEditRows] = useState({}); // key: id, value: { od, wc, in, dd, rate }

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const getBillItemReference = (record) =>
    record?.app_originpackagetransportation?.app_originpackage?.reference ?? String(record?.id ?? "");

  const getBillId = () => companyBillItemId ?? dataCompanyBill?.id;

  const onDeleteBillItem = async (record) => {
    const billId = getBillId();
    if (!billId || !record?.id) return;

    try {
      setLoading(true);
      if (isCompanyBill) {
        await companyBillService.deleteCompanyBillItem(accessToken, {
          id: record.id,
          company_bill_id: billId,
        });
        await fetchCompanyBillItem({ ...search, company_bill_no: billId });
      } else {
        await customerBillService.deleteCustomerBillItem(accessToken, {
          id: record.id,
          customer_bill_id: billId,
        });
        await fetchCustomerBillItem({ ...search, customer_bill_no: billId });
      }
      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        <>{"Success."}</>,
        <>{t("Origin_Package_Service_Table.Delete_Success", { ns: translations })}</>
      );
    } catch (err) {
      console.error("[CreateOriginPackageTransportationService] onDeleteBillItem: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const isLockedOriginPackageRow = (row) =>
    row?.description === "origin_package_service" && !row?.app_originpackagetransportation?.app_originpackage;

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // helper รวม total ของแต่ละแถว
  const calcRowTotal = (row) => {
    const originPackage = row.app_originpackagetransportation?.app_originpackage ?? {};
    const originService = originPackage.app_originpackageservice ?? [];
    const getServiceCost = (type) => Number(originService.find((s) => s.package_service === type)?.service_cost || 0);
    const od = getServiceCost("OD");
    const wc = getServiceCost("WC");
    const inVal = getServiceCost("IN");
    const ot = getServiceCost("OT");
    const dd = Number(row.app_destinationpackageservice?.service_cost || 0);
    const rate = Number(row?.rate || 0);
    const shippingCost = Number(row.bill_amount || 0); // ใช้จาก field bill_amount

    return od + wc + inVal + ot + dd + shippingCost;
    // return (od + wc + inVal + dd) * rate + shippingCost;
  };

  // helper ดึงค่า service แต่ละประเภทจากแถว
  const getServiceCostFromRow = (row, type) => {
    const ops = row.app_originpackagetransportation?.app_originpackage?.app_originpackageservice ?? [];
    return Number(ops.find((s) => s.package_service === type)?.service_cost || 0);
  };

  // ---- helper: สร้าง summary เพื่อส่งให้ parent ----
  const buildSummaryFromRows = (rows) => {
    const items = rows.map((row) => {
      const total_od = getServiceCostFromRow(row, "OD");
      const total_wc = getServiceCostFromRow(row, "WC");
      const total_in = getServiceCostFromRow(row, "IN");
      const total_ot = getServiceCostFromRow(row, "OT");
      const dd = Number(row.app_destinationpackageservice?.service_cost || 0);
      return { ...row, total_od, total_wc, total_in, total_ot, package_service_dd: dd };
    });

    const packageTotal = items.reduce((sum, r) => sum + Number(r.bill_amount || 0), 0); // รวม shipping
    const sumTotal = items.reduce(
      (sum, r) =>
        sum +
        Number(r.total_od || 0) +
        Number(r.total_wc || 0) +
        Number(r.total_in || 0) +
        Number(r.total_ot || 0) +
        Number(r.package_service_dd || 0),
      0
    );
    const totalPrice = sumTotal + packageTotal;

    return { items, sumTotal, packageTotal, totalPrice };
  };

  // helper รวม total ทั้งหมด
  const calcTotalAll = () => {
    return companyBillItem.reduce((sum, row) => sum + calcRowTotal(row), 0);
  };

  // Event แก้ไขแต่ละช่อง
  const handleInputChange = (value, id, field) => {
    setCompanyBillItem((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (field === "rate") {
          return {
            ...item,
            rate: value,
          };
        }

        if (field === "dd") {
          if (!canEditPackageServiceDd(item)) return item;

          const originPackage = item.app_originpackagetransportation?.app_originpackage ?? {};
          const destPackages = originPackage.app_destinationpackage;
          const firstDestPkg = Array.isArray(destPackages) && destPackages.length > 0 ? destPackages[0] : null;
          const existingDd = item.app_destinationpackageservice ?? {};

          return {
            ...item,
            app_destinationpackageservice: {
              ...existingDd,
              package_service: existingDd.package_service ?? "DD",
              service_cost: value,
              destination_package_id: existingDd.destination_package_id ?? firstDestPkg?.id ?? null,
            },
          };
        }

        // OD / WC / IN / OT (origin package service)
        const originPackage = item.app_originpackagetransportation?.app_originpackage ?? {};
        const originServices = originPackage.app_originpackageservice ?? [];

        const serviceIndex = originServices.findIndex((s) => s.package_service === field.toUpperCase());

        let updatedServices;
        if (serviceIndex >= 0) {
          // เคส: มีอยู่แล้ว → อัปเดต
          updatedServices = originServices.map((s, i) => (i === serviceIndex ? { ...s, service_cost: value } : s));
        } else {
          // เคส: ไม่มีเลย → เพิ่มใหม่
          updatedServices = [
            ...originServices,
            {
              package_service: field.toUpperCase(),
              service_cost: value,
            },
          ];
        }

        return {
          ...item,
          app_originpackagetransportation: {
            ...item.app_originpackagetransportation,
            app_originpackage: {
              ...originPackage,
              app_originpackageservice: updatedServices,
            },
          },
        };
      })
    );
  };

  /** Resolve destination package + DD service ids for save payload. */
  const resolveDestinationFields = (row) => {
    const originPackage = row.app_originpackagetransportation?.app_originpackage ?? {};
    const destPackages = originPackage.app_destinationpackage;
    const firstDestPkg = Array.isArray(destPackages) && destPackages.length > 0 ? destPackages[0] : null;
    const ddService = row.app_destinationpackageservice;

    return {
      destination_package_service_id: row.destination_package_service_id ?? ddService?.id ?? null,
      destination_package_id: firstDestPkg?.id ?? ddService?.destination_package_id ?? null,
    };
  };

  /** DD editable only when destination package or linked DD service exists. */
  const canEditPackageServiceDd = (row) => {
    const { destination_package_id, destination_package_service_id } = resolveDestinationFields(row);
    return (
      (destination_package_id != null && Number(destination_package_id) > 0) ||
      (destination_package_service_id != null && Number(destination_package_service_id) > 0)
    );
  };

  /** Coerce Prisma Decimal / string from API to finite number for bill payload. */
  const toServiceCostNumber = (raw) => {
    if (raw == null || raw === "") return 0;
    if (typeof raw === "object" && raw !== null && typeof raw.toString === "function") {
      const n = Number(raw.toString());
      return Number.isFinite(n) ? n : 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * Before save: if bill line has origin package but no OT row in nested state,
   * append OT @ 0 so payload always sends package_service_ot (backend create/update).
   */
  const ensureOtOriginServiceOnRow = (row) => {
    const pkg = row?.app_originpackagetransportation?.app_originpackage;
    const originPkId = Number(pkg?.id ?? row?.app_originpackagetransportation?.origin_package_id ?? 0);
    if (!pkg || !Number.isFinite(originPkId) || originPkId <= 0) return row;

    const services = [...(pkg.app_originpackageservice ?? [])];
    if (services.some((s) => s.package_service === "OT")) return row;

    return {
      ...row,
      app_originpackagetransportation: {
        ...row.app_originpackagetransportation,
        app_originpackage: {
          ...pkg,
          app_originpackageservice: [...services, { package_service: "OT", service_cost: 0 }],
        },
      },
    };
  };

  // ปุ่ม save
  const handleSave = async () => {
    const rowsForPayload = companyBillItem.map(ensureOtOriginServiceOnRow);

    const payload = rowsForPayload.map((row) => {
      const originPackage = row.app_originpackagetransportation?.app_originpackage || {};
      const { destination_package_id, destination_package_service_id } = resolveDestinationFields(row);
      const originService = originPackage.app_originpackageservice || [];
      const getServiceCost = (type) => {
        const found = originService.find((s) => s.package_service === type);
        return toServiceCostNumber(found?.service_cost);
      };

      const packageServiceOD = getServiceCost("OD");
      const packageServiceWC = getServiceCost("WC");
      const packageServiceIN = getServiceCost("IN");
      const packageServiceOT = getServiceCost("OT");
      const packageServiceDD = toServiceCostNumber(row.app_destinationpackageservice?.service_cost);
      const rate = Number(row.rate || 0);
      const calShippingCost = row.bill_amount;
      const totalBill =
        packageServiceOD + packageServiceWC + packageServiceIN + packageServiceOT + packageServiceDD + calShippingCost;
      const totalBillNumber = parseFloat(totalBill);
      const totalBillFormatted = Number.isFinite(totalBillNumber) ? totalBillNumber.toFixed(3) : "0.000";
      const totalWeight = Number(originPackage.total_weight || 0);
      const totalVolume = Number(originPackage.total_volume || 0);
      return {
        id: row.id,
        bill_amount: calShippingCost,
        package_service_od: packageServiceOD,
        package_service_wc: packageServiceWC,
        package_service_in: packageServiceIN,
        package_service_ot: packageServiceOT,
        package_service_dd: packageServiceDD,
        rate: rate,
        origin_package_id: originPackage?.id ?? row?.app_originpackagetransportation?.origin_package_id ?? null,
        origin_package_transportation_id: row?.origin_package_transportation_id,
        destination_package_service_id,
        destination_package_id,
        total_bill: totalBillFormatted,
        density: row.density,
        total_weight: totalWeight,
        total_volume: totalVolume,
      };
    });
    try {
      setLoading(true);

      // 1) ยิง API ตามเดิม
      let response;
      if (isCompanyBill) {
        response = await companyBillService.updateCompanyBillItem(accessToken, payload);
      } else {
        response = await customerBillService.updateCustomerBillItem(accessToken, payload);
      }

      // 2) อัปเดต state ให้สะท้อนค่าที่เพิ่งบันทึก (bill_amount/rate/DD/OT) ทันที (เพื่อ UI อัปเดตทันที)
      const updatedRows = rowsForPayload.map((row) => {
        const p = payload.find((x) => x.id === row.id);
        if (!p) return row;

        const opt = row.app_originpackagetransportation;
        const originPackage = opt?.app_originpackage ?? {};
        const originServices = [...(originPackage.app_originpackageservice ?? [])];
        const otIdx = originServices.findIndex((s) => s.package_service === "OT");
        const nextOtServices =
          otIdx >= 0
            ? originServices.map((s, i) => (i === otIdx ? { ...s, service_cost: p.package_service_ot } : s))
            : [...originServices, { package_service: "OT", service_cost: p.package_service_ot }];

        const destFields = resolveDestinationFields(row);

        return {
          ...row,
          bill_amount: p.bill_amount,
          rate: p.rate,
          destination_package_service_id:
            row.destination_package_service_id ?? destFields.destination_package_service_id,
          app_destinationpackageservice: {
            ...(row.app_destinationpackageservice || {}),
            package_service: row.app_destinationpackageservice?.package_service ?? "DD",
            service_cost: p.package_service_dd,
            id: row.app_destinationpackageservice?.id ?? destFields.destination_package_service_id ?? null,
            destination_package_id:
              row.app_destinationpackageservice?.destination_package_id ?? destFields.destination_package_id ?? null,
          },
          ...(opt
            ? {
                app_originpackagetransportation: {
                  ...opt,
                  app_originpackage: {
                    ...originPackage,
                    app_originpackageservice: nextOtServices,
                  },
                },
              }
            : {}),
        };
      });
      setCompanyBillItem(updatedRows);

      // 3) คำนวณสรุป & เรียก onItemsChange → CompanyBiller เห็น OD/WC/IN/Total ใหม่ทันที
      const summary = buildSummaryFromRows(updatedRows);
      onItemsChange?.(summary);

      // 4) แจ้งผล
      if (response !== null && response !== undefined) {
        openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Update bill success"}</>);
      }

      // 5) (คงพฤติกรรมเดิม) refetch เพื่อ sync กับฐานข้อมูลภายหลัง
      if (isCompanyBill) {
        const param = { company_bill_no: dataCompanyBill.id, limit: 10, page: 1 };
        fetchCompanyBillItem(param);
      } else {
        const param = { customer_bill_no: dataCompanyBill.id, limit: 10, page: 1 };
        fetchCustomerBillItem(param);
      }
    } catch (err) {
      console.error("[CreateOriginPackageTransportationService] handleSave: ", err);
      setLoading(false);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  /** About layout table */
  const centerTitle = (text) => (
    <div
      style={{
        textAlign: "center",
        width: "100%",
        fontWeight: 600,
      }}
    >
      {text}
    </div>
  );

  const renderColumnTitle = (title) => {
    const parts = String(title).split(" ");

    return (
      <div
        style={{
          whiteSpace: "normal",
          lineHeight: "14px",
          textAlign: "center",
          wordBreak: "break-word",
        }}
      >
        {parts.map((part, index) => (
          <div key={index}>{part}</div>
        ))}
      </div>
    );
  };
  // format number on render table
  const renderInputNumber = ({ value, record, field, disabled = false, precision = 2, max = 99999 }) => {
    return (
      <InputNumber
        style={{
          width: "100%",
        }}
        controls={false}
        disabled={disabled}
        min={0}
        max={max}
        precision={precision}
        value={value}
        stringMode={false}
        onChange={(value) => {
          if (value === null || value === undefined) {
            handleInputChange(0, record.id, field);
            return;
          }

          // บังคับจำนวนทศนิยม
          const fixed = Number(Number(value).toFixed(precision));

          handleInputChange(fixed, record.id, field);
        }}
        // formatter={(value) => `${value}`.replace(/\B(?=(\d{2})+(?!\d))/g, ",")}
      />
    );
  };

  const columns = [
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Origin_Service`, { ns: translations })),
      dataIndex: "origin_service",
      key: "origin_service",
      width: 150,
      render: (_, record) => {
        let originpackage = record?.app_originpackagetransportation?.app_originpackage;
        let origintransportation = record?.app_originpackagetransportation?.app_origintransportation;
        return (
          <div>
            {originpackage && origintransportation
              ? originpackage.reference + " / " + origintransportation.container
              : "-"}
          </div>
        );
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Package_Type`, { ns: translations })),
      dataIndex: "package_type",
      key: "package_type",
      width: 60,
      render: (_, record) => {
        const originPackage = record?.app_originpackagetransportation?.app_originpackage;
        return <div className="text-center">{originPackage?.package_type ? originPackage.package_type : "-"}</div>; // Display the actual description
      },
    },
    {
      title: renderColumnTitle(t(`Origin_Package_Service_Table.Table_OD_CNY`, { ns: translations })),
      dataIndex: "note",
      key: "note",
      width: 70,
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        const value = odPackage?.note ?? `-`;
        return <div className="text-right  whitespace-nowrap"> {value}</div>;
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_OD`, { ns: translations })),
      dataIndex: "package_service_od",
      key: "package_service_od",
      width: 100,
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        return renderInputNumber({
          value: odPackage?.service_cost || 0,
          record,
          field: "od",
          disabled: isLockedOriginPackageRow(record),
          precision: 2,
          max: 99999,
        });
      },
    },
    {
      title: renderColumnTitle(t(`Origin_Package_Service_Table.Table_WC_CNY`, { ns: translations })),
      dataIndex: "note",
      key: "note",
      width: 70,
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const odPackage = originPackageService.find((item) => item.package_service === "WC");
        const value = odPackage?.note ?? `-`;
        return <div className="text-right whitespace-nowrap"> {value}</div>;
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_WC`, { ns: translations })),
      dataIndex: "package_service_wc",
      key: "package_service_wc",
      width: 100,
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const wcPackage = originPackageService.find((item) => item.package_service === "WC");
        return renderInputNumber({
          value: wcPackage?.service_cost || 0,
          record,
          field: "wc",
          disabled: isLockedOriginPackageRow(record),
          precision: 2,
          max: 99999,
        });
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_DD`, { ns: translations })),
      dataIndex: "package_service_dd",
      key: "package_service_dd",
      width: 100,
      render: (_, record) => {
        const destinationPackageService = record.app_destinationpackageservice;
        return renderInputNumber({
          value: Number(destinationPackageService?.service_cost || 0),
          record,
          field: "dd",
          disabled: isLockedOriginPackageRow(record) || !canEditPackageServiceDd(record),
          precision: 0,
          max: 99999,
        });
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Volume`, { ns: translations })),
      dataIndex: "total_volume",
      key: "total_volume",
      width: 90,
      render: (_, record) => {
        const value = record.app_originpackagetransportation?.app_originpackage?.total_volume || 0;
        return (
          <div className="text-right  whitespace-nowrap"> {formatNumber(Math.round(value * 100000) / 100000, 6)}</div>
        );
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Weight`, { ns: translations })),
      dataIndex: "total_weight",
      key: "total_weight",
      width: 90,
      render: (_, record) => {
        const value = record.app_originpackagetransportation?.app_originpackage?.total_weight || 0;
        // return <div className="text-right">{formatNumber(value, 6, "round")}</div>;
        return <div className="text-right whitespace-nowrap"> {formatNumber(Math.round(value * 100) / 100, 2)}</div>;
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Density`, { ns: translations })),
      dataIndex: "density_kg_cbm",
      key: "density_kg_cbm",
      width: 90,
      render: (_, record) => {
        const value = record.density || 0;
        // return <div className="text-right">{formatNumber(value, 6, "round")}</div>;
        return <div className="text-right whitespace-nowrap"> {formatNumber(Math.round(value * 100) / 100, 2)}</div>;
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Rate`, { ns: translations })),
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (_, record) => {
        const originPackageTransportation = record?.app_originpackagetransportation;
        const rateOriginPackageData = isCompanyBill
          ? originPackageTransportation?.is_company_weight_formula
            ? originPackageTransportation?.apply_company_weight_formula
            : originPackageTransportation?.apply_company_volume_formula
          : originPackageTransportation?.is_customer_weight_formula
            ? originPackageTransportation?.apply_customer_weight_formula
            : originPackageTransportation?.apply_customer_volume_formula;

        // substring หลังเครื่องหมาย "="
        const rateString = String(rateOriginPackageData || "").split("=")[1] || "";
        // แปลงเป็น array ตัวเลข
        const rateValues = rateString.split("/").map((v) => Number(v.trim()));

        // mapping ตามลำดับ (A, B, C, D, E)
        const rateMap = {
          A: rateValues[0] || 0,
          B: rateValues[1] || 0,
          C: rateValues[2] || 0,
          D: rateValues[3] || 0,
          E: rateValues[4] || 0,
        };

        // ดึงตาม package_type (เช่น 'A', 'B', 'C', 'E')
        const pkgType = record?.app_originpackagetransportation?.app_originpackage?.package_type;
        const rateFromPackageType = rateMap[pkgType] ?? 0;

        const rateData = record?.rate === 0 ? rateFromPackageType : record?.rate;
        return renderInputNumber({
          value: rateData,
          record,
          field: "rate",
          disabled: isLockedOriginPackageRow(record),
          precision: 4,
          // max: 9999,
        });
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Shipping_Cost`, { ns: translations })),
      dataIndex: "bill_amount",
      key: "bill_amount",
      width: 90,
      render: (_, record) => {
        const shippingCost = record.bill_amount || 0;
        return (
          <div className="text-right whitespace-nowrap">
            {" "}
            {formatNumber(Math.round(shippingCost * 10000) / 10000, 4)}
          </div>
        );
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Other_Fee`, { ns: translations })),
      dataIndex: "package_service_ot",
      key: "package_service_ot",
      width: 100,
      render: (_, record) => {
        const originPackage = record.app_originpackagetransportation?.app_originpackage ?? {};
        const originPackageService = originPackage.app_originpackageservice ?? [];
        const otPackage = originPackageService.find((item) => item.package_service === "OT");
        return renderInputNumber({
          value: otPackage?.service_cost ?? 0,
          record,
          field: "ot",
          disabled: isLockedOriginPackageRow(record),
          precision: 2,
          max: 999,
        });
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Total_Bill`, { ns: translations })),
      dataIndex: "total_bill",
      key: "total_bill",
      width: 100,
      // ellipsis: true,
      render: (_, record) => {
        const originPackage = record?.app_originpackagetransportation?.app_originpackage ?? {};
        const calShippingCost = record.bill_amount;
        const originPackageService = originPackage?.app_originpackageservice ?? [];
        const wcPackage = originPackageService.find((item) => item.package_service === "WC");
        const odPackage = originPackageService.find((item) => item.package_service === "OD");
        const inPackage = originPackageService.find((item) => item.package_service === "IN");
        const otPackage = originPackageService.find((item) => item.package_service === "OT");
        const destinationPackageService = record.app_destinationpackageservice;
        const ddPackage = Number(destinationPackageService?.service_cost || 0);
        const totalBill =
          Number(odPackage?.service_cost || 0) +
          Number(wcPackage?.service_cost || 0) +
          Number(inPackage?.service_cost || 0) +
          Number(otPackage?.service_cost || 0) +
          Number(ddPackage) +
          Number(calShippingCost);
        return (
          <div className="text-right whitespace-nowrap"> {formatNumber(Math.round(totalBill * 10000) / 10000, 4)}</div>
        );
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Note`, { ns: translations })),
      dataIndex: "note",
      key: "note",
      width: 120,
      ellipsis: true,
      render: (_, record) => {
        return (
          <div
            className="text-center"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              minWidth: 120,
            }}
          >
            {record.note || "-"}
          </div>
        ); // Display the actual description
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Created_Date`, { ns: translations })),
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      // ellipsis: true,
      render: (_, record) => {
        const created_at = record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return (
          <div
            className="text-center"
            style={{
              whiteSpace: "normal",
              // wordBreak: "break-word",
              minWidth: 120,
            }}
          >
            {created_at}
          </div>
        );
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Updated_Date`, { ns: translations })),
      dataIndex: "updated_at",
      key: "updated_at",
      width: 150,
      // ellipsis: true,
      render: (_, record) => {
        const updated_at = record.updated_at != null ? dayjs(record.updated_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return (
          <div
            className="text-center"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              minWidth: 120,
            }}
          >
            {updated_at}
          </div>
        ); // Display the actual description
      },
    },
    /** TODO : Remove -> waiting for approve by P'Ann
    {
      title: t("Origin_Package_Service_Table.Table_ID", { ns: translations }),
      width: 150,
      dataIndex: "id",
      key: "id",
      render: (_, record) => {
        return <p>{record.id}</p>;
      },
    },
    {
      title: t("Origin_Package_Service_Table.Table_Description", { ns: translations }),
      width: 150,
      dataIndex: "description",
      key: "description",
      render: (_, record) => {
        return <p>{record.description}</p>;
      },
    },
     */

    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Updated_By`, { ns: translations })),
      dataIndex: "updated_by",
      key: "updated_by",
      width: 150,
      // ellipsis: true,
      render: (_, record) => {
        return (
          <div
            className="text-center"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              minWidth: 120,
            }}
          >
            {record.updated_by || "-"}
          </div>
        ); // Display the actual description
      },
    },
    {
      title: centerTitle(t(`Origin_Package_Service_Table.Table_Delete`, { ns: translations })),
      dataIndex: "delete",
      key: "delete",
      width: 80,
      fixed: "right",
      render: (_, record) => {
        const reference = getBillItemReference(record);
        const canDelete = hasPermission(
          isCompanyBill ? permissions.CompanyBillItem.DELETE : permissions.CustomerBillItem.DELETE
        );

        if (!canDelete) {
          return null;
        }

        return (
          <Popconfirm
            title={t("Origin_Package_Service_Table.Confirm_Delete", {
              ns: translations,
              reference,
            })}
            okText={t("BTN_OK", { ns: "buttons" })}
            cancelText={t("BTN_Cancel", { ns: "buttons" })}
            onConfirm={() => onDeleteBillItem(record)}
            getPopupContainer={() => document.body}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        );
      },
    },
  ];

  const fetchCompanyBillItem = useCallback(
    async (param) => {
      const params = {
        ...param,
        company_bill_no: companyBillItemId ?? dataCompanyBill.id,
      };

      try {
        const data = await companyBillService.getCompanyBillItem(accessToken, params);
        // ✅ Add default fields OD, WD, IN if not exist
        const mappedItems = (data?.query ?? []).map((item) => {
          const total_od = getServiceCostFromRow(item, "OD");
          const total_wc = getServiceCostFromRow(item, "WC");
          const total_in = getServiceCostFromRow(item, "IN");
          const total_ot = getServiceCostFromRow(item, "OT");
          return {
            ...item,
            total_od,
            total_wc,
            total_in,
            total_ot,
          };
        });

        dataCompanyBill.app_companybillitem = data?.query ?? [];
        let sum_total_bill = 0.0;
        data?.query?.map((value) => {
          const originService = value.app_originpackagetransportation?.app_originpackage?.app_originpackageservice;
          if (originService) {
            sum_total_bill = originService.reduce((sum, item) => sum + Number(item.service_cost), 0);
            dataCompanyBill.app_companybillitem.map((item) => {
              if (item.id === value.id) {
                item.rate = value.rate;
                item.bill_amount = value.bill_amount;
              }
            });
          }
        });

        const package_total_bill_amount = data?.query?.reduce((sum, item) => sum + Number(item.bill_amount), 0);
        dataCompanyBill.package_total_bill_amount = package_total_bill_amount;
        dataCompanyBill.sum_total_bill = Number(sum_total_bill) + Number(package_total_bill_amount);
        setCompanyBillItem(mappedItems);
        setTotal(data?.totalCount ?? 0);
        setSumTotal(data?.sumTotal ?? 0);
        setSearch({ ...params });
        onItemsChange({
          items: mappedItems,
          sumTotal: dataCompanyBill.package_total_bill_amount,
          packageTotal: package_total_bill_amount,
          totalPrice: dataCompanyBill.sum_total_bill,
        });
      } catch (err) {
        console.error("[CreateOriginPackageTransportationService] fetchCompanyBillItem: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, companyBillItemId, dataCompanyBill?.id, onItemsChange]
  );

  const fetchCustomerBillItem = useCallback(
    async (param) => {
      const params = {
        ...param,
        customer_bill_no: companyBillItemId ?? dataCompanyBill.id,
      };
      try {
        const data = await customerBillService.getCustomerBillItem(accessToken, params);
        // ✅ Add default fields OD, WD, IN if not exist
        const mappedItems = (data?.query ?? []).map((item) => {
          const total_od = getServiceCostFromRow(item, "OD");
          const total_wc = getServiceCostFromRow(item, "WC");
          const total_in = getServiceCostFromRow(item, "IN");
          const total_ot = getServiceCostFromRow(item, "OT");
          return {
            ...item,
            total_od,
            total_wc,
            total_in,
            total_ot,
          };
        });

        dataCompanyBill.app_customerbillitem = data?.query ?? [];
        let sum_total_bill = 0.0;
        data?.query?.map((value) => {
          const originService = value.app_originpackagetransportation?.app_originpackage?.app_originpackageservice;
          if (originService) {
            sum_total_bill = originService.reduce((sum, item) => sum + Number(item.service_cost || 0), 0);
            dataCompanyBill.app_customerbillitem.map((item) => {
              if (item.id === value.id) {
                item.rate = value.rate;
                item.bill_amount = value.bill_amount;
              }
            });
          }
        });
        const package_total_bill_amount = data?.query?.reduce((sum, item) => sum + Number(item.bill_amount), 0);
        dataCompanyBill.package_total_bill_amount = package_total_bill_amount;
        dataCompanyBill.sum_total_bill = Number(sum_total_bill) + Number(package_total_bill_amount);
        dataCompanyBill.total_price = dataCompanyBill.sum_total_bill;
        setCompanyBillItem(mappedItems);
        setTotal(data?.totalCount ?? 0);
        setSumTotal(data?.sumTotal ?? 0);
        setSearch({ ...param });
        onItemsChange({
          items: mappedItems,
          sumTotal: dataCompanyBill.package_total_bill_amount,
          packageTotal: package_total_bill_amount,
          totalPrice: dataCompanyBill.sum_total_bill,
        });
      } catch (err) {
        console.error("[CreateOriginPackageTransportationService] fetchCustomerBillItem: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    if (!accessToken || hasFetchedRef.current) return;
    if (isCompanyBill) {
      fetchCompanyBillItem(initPage);
    } else {
      fetchCustomerBillItem(initPage);
    }
    hasFetchedRef.current = true;
  }, [accessToken, isCompanyBill]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row align="middle" justify="end">
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={Array.isArray(companyBillItem) ? companyBillItem : []}
              rowKey={(record) => record.id}
              style={{ cursor: "pointer" }}
              tableLayout="fixed"
              // scroll={{ x: "max-content" }}
              scroll={{ x: 1400 }}
              pagination={{
                current: search.page,
                pageSize: search.limit,
                total: total,
                onChange: (page, limit) => {
                  if (isCompanyBill) {
                    fetchCompanyBillItem({ ...search, page, limit });
                  } else {
                    fetchCustomerBillItem({ ...search, page, limit });
                  }
                },
                showSizeChanger: true,
              }}
              footer={() => (
                <Row justify="end" style={{ marginTop: 8 }}>
                  <Col>
                    <b>
                      Total:{" "}
                      {sumTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 4,
                      })}
                    </b>
                  </Col>
                </Row>
              )}
            />
            {hasPermission(isCompanyBill ? permissions.CompanyBillItem.ADD : permissions.CustomerBillItem.ADD) && (
              <Row justify="center" style={{ marginTop: 16 }}>
                <Button type="primary" onClick={handleSave}>
                  Save Services
                </Button>
              </Row>
            )}
          </Col>
          <ModalDestinationService
            isCompanyBill={isCompanyBill}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            handleOk={handleOk}
            handleCancel={handleCancel}
            fetchCompanyBillItem={fetchCompanyBillItem}
            fetchCustomerBillItem={fetchCustomerBillItem}
            item={item}
            setItem={setItem}
            isEdit={isEdit}
            companyBillItem={companyBillItem[0]}
          />
        </Row>
      </Spin>
    </>
  );
};
